/**
 * User Access Helpers
 * Utilities for checking user access based on package_tier instead of account_type
 * 
 * Migration: Phase 8.4 - Remove account_type complexity
 * Replaces account_type checks with organization-level package_tier checks
 */

import { getApiBaseUrl } from './apiConfig';

/**
 * Check if a user should use self-assessment routes based on their organization's package_tier
 * @param {Object} user - User object with organization_id
 * @param {string} packageTier - Optional package tier (if already fetched)
 * @returns {Promise<boolean>} - True if user should use self-assessment routes
 */
// Roles that always access the full admin system, never self-assessment
const FULL_SYSTEM_ROLES = ['super_admin', 'admin', 'System Admin'];

// In-flight + short-TTL cache for /user/package-tier. The answer rarely changes
// per session, and many components call this helper on mount. Without dedup we
// see ~8 identical requests per dashboard navigation.
const _TIER_TTL_MS = 30_000;
const _tierCache = { inflight: null, value: null, at: 0 };
export function invalidatePackageTierCache() {
  _tierCache.inflight = null;
  _tierCache.value = null;
  _tierCache.at = 0;
}

async function _fetchPackageTier() {
  if (_tierCache.inflight) return _tierCache.inflight;
  if (_tierCache.value !== null && (Date.now() - _tierCache.at) < _TIER_TTL_MS) {
    return _tierCache.value;
  }
  _tierCache.inflight = (async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/user/package-tier`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) return null;
      const data = await response.json();
      _tierCache.value = data.package_tier ?? null;
      _tierCache.at = Date.now();
      return _tierCache.value;
    } catch (error) {
      console.error('Error fetching package tier:', error);
      return null;
    } finally {
      _tierCache.inflight = null;
    }
  })();
  return _tierCache.inflight;
}

export async function shouldUseSelfAssessmentRoutes(user, packageTier = null) {
  if (!user) return false;

  // Super admins / system admins always use the full system
  if (FULL_SYSTEM_ROLES.includes(user.role)) return false;

  // If no organization_id, use full account routes
  if (!user.organization_id) return false;

  // If package tier already provided, use it
  if (packageTier) {
    return ['demo', 'starter', 'professional', 'enterprise'].includes(packageTier);
  }

  const tier = await _fetchPackageTier();
  if (tier === null) return false;
  return ['demo', 'starter', 'professional', 'enterprise'].includes(tier);
}

/**
 * Synchronous version that uses cached package tier
 * Use this when package tier is already available in user object or state
 * @param {Object} user - User object
 * @param {string} userPackageTier - Package tier from user's organization
 * @returns {boolean} - True if user should use self-assessment routes
 */
export function shouldUseSelfAssessmentRoutesSync(user, userPackageTier = null) {
  if (!user) return false;

  // Super admins / system admins always use the full system
  if (FULL_SYSTEM_ROLES.includes(user.role)) return false;

  // If no organization_id, use full account routes
  if (!user.organization_id) return false;

  // If package tier provided, check it
  if (userPackageTier) {
    return ['demo', 'starter', 'professional', 'enterprise'].includes(userPackageTier);
  }

  // If user object has package_tier property (from API response)
  if (user.package_tier) {
    return ['demo', 'starter', 'professional', 'enterprise'].includes(user.package_tier);
  }

  // No package_tier info available — default to full system routes
  // Self-assessment users will always have package_tier set on their organization
  return false;
}

/**
 * Check if a user should use demo routes (legacy compatibility)
 * This is kept for backward compatibility during migration
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function shouldUseDemoRoutes(user) {
  // During migration, this checks both account_type (legacy) and package_tier (new)
  if (!user) return false;
  
  // Legacy check (for backward compatibility)
  if (user.account_type === 'self_assessor_demo' || 
      user.account_type === 'self_assessor' || 
      user.account_type === 'converted') {
    return true;
  }
  
  // New check based on package_tier
  return shouldUseSelfAssessmentRoutesSync(user, user.package_tier);
}

